import type { ArchetypeId, AxisId } from "@/lib/infiniteSelves/types";

/**
 * 8 Core Themes representing what an imagined identity/life may be pointing at.
 */
export type ThemeId =
  | "freedom"
  | "love"
  | "confidence"
  | "creativity"
  | "adventure"
  | "achievement"
  | "belonging"
  | "purpose";

export const THEME_ORDER: readonly ThemeId[] = [
  "freedom",
  "love",
  "confidence",
  "creativity",
  "adventure",
  "achievement",
  "belonging",
  "purpose",
] as const;

/**
 * 10 Real-world Aspiration areas for practical bridge building.
 */
export type AspirationId =
  | "health"
  | "craft"
  | "career"
  | "relationships"
  | "learning"
  | "adventure"
  | "money"
  | "community"
  | "calm"
  | "voice";

export const ASPIRATION_ORDER: readonly AspirationId[] = [
  "health",
  "craft",
  "career",
  "relationships",
  "learning",
  "adventure",
  "money",
  "community",
  "calm",
  "voice",
] as const;

/**
 * Time budget options for bridging daily and weekly habits.
 */
export type TimeBudgetId = "10min-day" | "30min-day" | "1hr-week" | "3hr-week";

export const TIME_BUDGET_ORDER: readonly TimeBudgetId[] = [
  "10min-day",
  "30min-day",
  "1hr-week",
  "3hr-week",
] as const;

/**
 * 8 Obstacles visitors face when grounding imagined values into action.
 */
export type ObstacleId =
  | "no-time"
  | "low-energy"
  | "fear-of-failing"
  | "perfectionism"
  | "self-doubt"
  | "distraction"
  | "others-opinions"
  | "losing-momentum";

export const OBSTACLE_ORDER: readonly ObstacleId[] = [
  "no-time",
  "low-energy",
  "fear-of-failing",
  "perfectionism",
  "self-doubt",
  "distraction",
  "others-opinions",
  "losing-momentum",
] as const;

/**
 * Optional sensitive self-reflection responses (never persisted, never in links).
 */
export type ReflectionControlAnswer =
  | "mostly in my control"
  | "sometimes hard to steer"
  | "often hard to stop"
  | "prefer not to say";

export type ReflectionAreaId = "sleep" | "work" | "study" | "relationships" | "goals";

export const REFLECTION_AREA_ORDER: readonly ReflectionAreaId[] = [
  "sleep",
  "work",
  "study",
  "relationships",
  "goals",
] as const;

export type ReflectionAreaAnswer = "no" | "a little" | "quite a bit" | "prefer not to say";

export interface ReflectionAnswers {
  control?: ReflectionControlAnswer | null;
  areas?: Partial<Record<ReflectionAreaId, ReflectionAreaAnswer>>;
}

export type ReflectionTone = "steady" | "gentle-note" | "support-note";

export interface ReflectionReply {
  tone: ReflectionTone;
  paragraphs: string[];
  showSupportSection: boolean;
}

/**
 * Persona descriptor for bridge interpretation & planning.
 */
export type BridgePersona =
  | {
      kind: "self";
      archetype: ArchetypeId;
      name: string;
      title: string;
      occupation: string;
      scenarioLabel: string;
      topAxis: AxisId;
    }
  | {
      kind: "own";
    };

/**
 * Lightweight summary of an alternate self for the store to present to the bridge.
 * Derived solely from free-sample fields.
 */
export interface PersonaSummary {
  index: 0 | 1 | 2 | 3;
  archetype: ArchetypeId;
  name: string;
  title: string;
  occupation: string;
  scenarioLabel: string;
  universeName: string;
  topAxis: AxisId;
  tagline: string;
}

/**
 * Persona choice stored in BridgeState.
 */
export type PersonaChoice =
  | {
      kind: "self";
      index: 0 | 1 | 2 | 3;
    }
  | {
      kind: "own";
    };

/**
 * Interpretation of an imagined identity.
 */
export interface BridgeInterpretation {
  id: string;
  text: string;
  bullets: string[];
  caveat: string;
}

/**
 * Concrete goal in the generated plan.
 */
export interface PlanGoal {
  id: string;
  aspirationId: AspirationId;
  title: string;
  why: string;
  measure: string;
}

/**
 * A concrete 10-minute action.
 */
export interface PlanTenMinuteAction {
  title: string;
  steps: string[];
}

/**
 * An anchoring habit.
 */
export interface PlanHabit {
  cadence: "daily" | "weekly";
  text: string;
  cue: string;
}

/**
 * A step in the 7-day sequence.
 */
export interface SevenDayStep {
  day: number; // 1..7
  title: string;
  task: string;
  minutes: number;
}

/**
 * Tailored obstacle response in the plan.
 */
export interface PlanObstacle {
  id: ObstacleId;
  label: string;
  response: string[];
}

/**
 * Clarification separating the fantasy realm from real agency.
 */
export interface ImaginedVsChangeable {
  imagined: string[];
  changeable: string[];
  note: string;
}

/**
 * The complete deterministic Dream-to-Reality bridge plan.
 */
export interface DreamPlan {
  engineVersion: string;
  planId: string;
  goals: [PlanGoal, PlanGoal, PlanGoal];
  tenMinuteAction: PlanTenMinuteAction;
  habit: PlanHabit;
  sevenDay: SevenDayStep[]; // exactly 7
  obstacles: PlanObstacle[]; // 3
  imaginedVsChangeable: ImaginedVsChangeable;
  honestLimit: string;
  usedFreeText: false;
}

/**
 * Input needed to generate a DreamPlan.
 */
export interface GeneratePlanInput {
  persona: BridgePersona;
  themes: ThemeId[];
  aspirations: AspirationId[]; // 1..3
  approvedInterpretation: string; // non-empty approved text
  timeBudget: TimeBudgetId;
  obstacles?: ObstacleId[]; // 0..3, defaults to top 3 if empty
}

/**
 * Goal progress state: each goal has 3 progress check-steps.
 */
export type GoalStepsProgress = [boolean, boolean, boolean];

/**
 * Bridge persistent state.
 */
export interface BridgeState {
  engineVersion: string;
  personaChoice: PersonaChoice | null;
  freeText: string;
  themes: ThemeId[];
  aspirations: AspirationId[];
  interpretationDecision: "pending" | "accepted" | "reworded" | "rejected";
  rewordedText: string;
  timeBudget: TimeBudgetId | null;
  obstacles: ObstacleId[];
  progress: {
    planId: string;
    daysDone: [boolean, boolean, boolean, boolean, boolean, boolean, boolean];
    goalSteps: [GoalStepsProgress, GoalStepsProgress, GoalStepsProgress];
  };
  weeklyReflections: { weekLabel: string; text: string }[];
  includeInDossier: boolean;
}

/**
 * Dossier chapter export data for PDF inclusion.
 */
export interface DossierChapterData {
  engineVersion: string;
  personaLabel: string;
  aspirationsLabels: string[];
  approvedInterpretation: string;
  plan: DreamPlan;
}
